import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  Search,
  CheckCircle,
  Clock,
  ArrowLeft,
  RefreshCw,
  Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  "portaria-remota": "Portaria Remota",
  "monitoramento-24h": "Monitoramento 24h",
  "cameras-alarmes": "Cameras e Alarmes",
  "cerca-eletrica": "Cerca Eletrica",
  "outro": "Outro",
};

export default function AdminPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contacts`);
      setContacts(res.data);
    } catch {
      toast.error("Erro ao carregar contatos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const toggleStatus = async (contact) => {
    const newStatus = contact.status === "pending" ? "respondido" : "pending";
    setUpdatingId(contact.id);
    try {
      await axios.patch(`${API}/contacts/${contact.id}/status`, { status: newStatus });
      setContacts((prev) =>
        prev.map((c) => (c.id === contact.id ? { ...c, status: newStatus } : c))
      );
      toast.success(
        newStatus === "respondido"
          ? "Marcado como respondido!"
          : "Marcado como pendente."
      );
    } catch {
      toast.error("Erro ao atualizar status.");
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = contacts
    .filter((c) => {
      if (filter === "pending") return c.status === "pending";
      if (filter === "respondido") return c.status === "respondido";
      return true;
    })
    .filter((c) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.phone.includes(q)
      );
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const pendingCount = contacts.filter((c) => c.status === "pending").length;
  const respondidoCount = contacts.filter((c) => c.status === "respondido").length;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="border-b border-neutral-800 bg-black/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="flex items-center gap-2 text-gray-400 hover:text-yellow-500 transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Voltar ao Site</span>
            </a>
            <div className="h-6 w-px bg-neutral-700" />
            <h1 className="text-lg md:text-xl font-bold">
              <span className="text-yellow-500">Painel</span> de Leads
            </h1>
          </div>
          <Button
            onClick={fetchContacts}
            variant="outline"
            size="sm"
            className="border-neutral-700 bg-neutral-900 text-gray-300 hover:bg-neutral-800 hover:text-white"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <motion.div
            className="bg-neutral-900 border border-neutral-800 rounded-xl p-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0 }}
            data-testid="admin-stat-total"
          >
            <p className="text-3xl font-extrabold text-yellow-500">{contacts.length}</p>
            <p className="text-sm text-gray-400 mt-1">Total de Leads</p>
          </motion.div>
          <motion.div
            className="bg-neutral-900 border border-neutral-800 rounded-xl p-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="admin-stat-pending"
          >
            <p className="text-3xl font-extrabold text-orange-400">{pendingCount}</p>
            <p className="text-sm text-gray-400 mt-1">Pendentes</p>
          </motion.div>
          <motion.div
            className="bg-neutral-900 border border-neutral-800 rounded-xl p-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            data-testid="admin-stat-responded"
          >
            <p className="text-3xl font-extrabold text-green-400">{respondidoCount}</p>
            <p className="text-sm text-gray-400 mt-1">Respondidos</p>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Buscar por nome, email ou telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 bg-neutral-900 border-neutral-700 text-white placeholder:text-gray-500"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex gap-2">
            <FilterBtn
              active={filter === "all"}
              onClick={() => setFilter("all")}
              testId="admin-filter-all"
            >
              <Filter className="w-4 h-4" /> Todos
            </FilterBtn>
            <FilterBtn
              active={filter === "pending"}
              onClick={() => setFilter("pending")}
              testId="admin-filter-pending"
            >
              <Clock className="w-4 h-4" /> Pendentes
            </FilterBtn>
            <FilterBtn
              active={filter === "respondido"}
              onClick={() => setFilter("respondido")}
              testId="admin-filter-responded"
            >
              <CheckCircle className="w-4 h-4" /> Respondidos
            </FilterBtn>
          </div>
        </div>

        {/* Contacts List */}
        {loading ? (
          <div className="text-center py-20 text-gray-500" data-testid="admin-loading">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-yellow-600" />
            Carregando leads...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-500" data-testid="admin-empty">
            Nenhum lead encontrado.
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-contacts-list">
            {filtered.map((contact, idx) => (
              <motion.div
                key={contact.id}
                className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 md:p-6"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                data-testid={`admin-contact-${contact.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-white truncate">
                        {contact.name}
                      </h3>
                      <span
                        className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${
                          contact.status === "respondido"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-orange-500/20 text-orange-400"
                        }`}
                        data-testid={`admin-status-badge-${contact.id}`}
                      >
                        {contact.status === "respondido" ? "Respondido" : "Pendente"}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-400 mb-3">
                      <span className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-yellow-600" />
                        {contact.email}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-yellow-600" />
                        {contact.phone}
                      </span>
                      {contact.service && (
                        <span className="text-yellow-500/80">
                          {SERVICE_LABELS[contact.service] || contact.service}
                        </span>
                      )}
                    </div>

                    <p className="text-gray-300 text-sm leading-relaxed">
                      {contact.message}
                    </p>

                    <p className="text-xs text-gray-600 mt-3">
                      {new Date(contact.created_at).toLocaleString("pt-BR")}
                    </p>
                  </div>

                  <Button
                    onClick={() => toggleStatus(contact)}
                    disabled={updatingId === contact.id}
                    size="sm"
                    className={`shrink-0 font-semibold ${
                      contact.status === "respondido"
                        ? "bg-neutral-800 text-gray-300 hover:bg-neutral-700"
                        : "bg-yellow-600 text-black hover:bg-yellow-700"
                    }`}
                    data-testid={`admin-toggle-${contact.id}`}
                  >
                    {updatingId === contact.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : contact.status === "respondido" ? (
                      <>
                        <Clock className="w-4 h-4 mr-1.5" /> Marcar Pendente
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1.5" /> Marcar Respondido
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function FilterBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? "bg-yellow-600 text-black"
          : "bg-neutral-900 text-gray-400 border border-neutral-700 hover:text-white"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
